/**
 * Office 服務 · 集中管理（Task-33 3.2e · SYSTEM_ADMIN）
 * func_code: system_office_management（is_mana · migration 077 seed）
 * 一頁掌握：Collabora infra 狀態 + 全租戶 Office 開關 + 各租戶健康重檢。
 * route path === func_code（選單 navigate(/${func_code})）。
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, Stack, Switch, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useMessage } from '../contexts/MessageContext';
import { usePermission } from '../hooks/usePermission';
import {
  getOfficeTenantOverview, setTenantOfficeEnabled, recheckTenantOfficeHealth,
  OfficeTenantOverview, OfficeHealthRecheck,
} from '../services/officeAdminService';

const FUNC_CODE = 'system_office_management';

const SystemOfficeManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showApiError } = useMessage();
  const { hasPermission, loading: permLoading } = usePermission();
  const canRead = hasPermission(FUNC_CODE, 'read');
  const canUpdate = hasPermission(FUNC_CODE, 'update');

  const tt = (k: string, fallback: string) => t(`officeManagement.${k}`, fallback);

  const [data, setData] = useState<OfficeTenantOverview | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyOrg, setBusyOrg] = useState<number | null>(null);
  // 各租戶 recheck 明細（org_id → 5 checks）
  const [rechecks, setRechecks] = useState<Record<number, OfficeHealthRecheck>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getOfficeTenantOverview());
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    if (!permLoading && canRead) load();
  }, [permLoading, canRead, load]);

  const handleToggle = async (orgId: number, enabled: boolean) => {
    setBusyOrg(orgId);
    try {
      await setTenantOfficeEnabled(orgId, enabled);
      showSuccess('SYS020002', { name: `Office ${enabled ? 'ON' : 'OFF'}` });
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyOrg(null);
    }
  };

  const handleRecheck = async (orgId: number) => {
    setBusyOrg(orgId);
    try {
      const h = await recheckTenantOfficeHealth(orgId);
      setRechecks((prev) => ({ ...prev, [orgId]: h }));
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyOrg(null);
    }
  };

  if (!permLoading && !canRead) {
    return <div className="page-container"><div className="error-message">{t('common.noPermission', '無此功能權限')}</div></div>;
  }

  const statusChip = (s: string) => {
    if (s === 'ok') return <Chip size="small" color="success" label={tt('statusOK', '✅ 正常')} />;
    if (s === 'off') return <Chip size="small" label={tt('statusOff', '未啟用')} />;
    if (s === 'warn') return <Chip size="small" color="warning" label={tt('statusWarn', '⚠️ 部分問題')} />;
    return <Chip size="small" color="error" label={tt('statusError', '❌ 失敗')} />;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>{tt('title', 'Office 服務 · 集中管理')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tt('subtitle', 'Collabora 基礎設施狀態 + 全租戶 Office 開關 + 健康檢查')}
      </Typography>

      {/* Section 1 · Collabora infra 狀態 */}
      <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
        <Typography variant="h6" gutterBottom>{tt('infraTitle', 'Collabora 基礎設施')}</Typography>
        {loading && !data ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Alert severity={data?.collabora_reachable ? 'success' : 'error'} sx={{ mb: 1 }}>
              URL: {data?.collabora_url || tt('notSet', '（未設定）')} ·{' '}
              {data?.collabora_reachable
                ? `${tt('reachable', '✅ 正常')}（${data?.collabora_latency_ms}ms）`
                : tt('unreachable', '❌ 無法連線')}
            </Alert>
            <Typography variant="caption" color="text.secondary">
              WOPI Secret: {data?.wopi_secret_set ? tt('secretSet', '✅ 已設') : tt('secretUnset', '❌ 未設')}
            </Typography>
          </>
        )}
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={load} disabled={loading}>🔄 {tt('reloadAll', '重新整理')}</Button>
        </Box>
      </Card>

      {/* Section 2 · 全租戶 table */}
      <Card variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{tt('colTenant', '租戶')}</TableCell>
              <TableCell>{tt('colEnabled', '啟用（可切換）')}</TableCell>
              <TableCell>{tt('colHealth', '健康狀態')}</TableCell>
              <TableCell>{tt('colActions', '操作')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.tenants?.map((row) => {
              const rc = rechecks[row.org_id];
              return (
                <TableRow key={row.org_id}>
                  <TableCell>{row.org_code} · {row.org_name}</TableCell>
                  <TableCell>
                    <Switch
                      color="success"
                      checked={row.office_enabled}
                      disabled={!canUpdate || busyOrg === row.org_id}
                      onChange={(e) => handleToggle(row.org_id, e.target.checked)} />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {statusChip(rc ? rc.overall_status : row.overall_status)}
                      {busyOrg === row.org_id && <CircularProgress size={14} />}
                    </Stack>
                    {rc && rc.checks.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {rc.checks.map((c) => (
                          <Typography key={c.name} variant="caption" display="block" color="text.secondary">
                            {c.status === 'ok' ? '✅' : c.status === 'warn' ? '⚠️' : c.status === 'off' ? '⚪' : '❌'} {c.name}
                            {c.latency_ms != null && ` (${c.latency_ms}ms)`}{c.hint ? ` · ${c.hint}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button size="small" disabled={busyOrg === row.org_id}
                      onClick={() => handleRecheck(row.org_id)}>
                      🔄 {tt('recheck', '重新檢查')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {(!data?.tenants || data.tenants.length === 0) && !loading && (
              <TableRow><TableCell colSpan={4}>
                <Typography variant="body2" color="text.secondary">{tt('empty', '無租戶')}</Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </Box>
  );
};

export default SystemOfficeManagementPage;
